import { FC, useMemo } from 'react';

import { EFontWeights } from '@appcharge/shared-ui';
import { Offer, OfferUI } from 'common/contracts';
import {
  ESaleDiscountDisplayType,
  ESpecialOfferInternalViewModel
} from 'constants/enums';
import { renderingUtil } from 'utils/renderingUtil';
import { PROMOTION_DEFAULTS } from 'views/OfferDesign/OfferDesign.consts';
import { normalizeBgColor } from 'views/OfferDesign/OfferDesign.utils';
import { OfferPreviewShell } from 'views/Offers/components/OfferPreviewShell';
import { usePriceForPreview } from 'views/Offers/hooks/usePriceForPreview';
import { buildProductsListForPreview } from 'views/Offers/Offer.utils';
import { resolveProgressBarPoints } from 'views/Offers/utils/resolveProgressBarPoints';

type PromotionPreviewProduct = ReturnType<
  typeof buildProductsListForPreview
>[number] & { strokedSaleQuantity?: string };

interface IProgressBarFormValues {
  progressBarId: string | null;
  progressBarPoints: number | null;
}

interface PromotionPreviewProps {
  getOfferDesign: { data?: OfferUI };
  productsListForPreview: PromotionPreviewProduct[];
  price: number;
  setAsFree: boolean;
  salePercentage: number;
  salePercentageDisplayType?: ESaleDiscountDisplayType;
  playerAvailability?: number;
  hidePlayerAvailability?: boolean;
  emblemBadges?: object[];
  ribbonBadges?: object[];
  progressBarFormValues?: IProgressBarFormValues;
  progressBarOffers?: Offer[];
}

const PROMOTION_PREVIEW_WIDTH = 450;

export const PromotionPreview: FC<PromotionPreviewProps> = ({
  getOfferDesign,
  productsListForPreview,
  price,
  setAsFree,
  salePercentage,
  salePercentageDisplayType,
  playerAvailability,
  hidePlayerAvailability = false,
  emblemBadges,
  ribbonBadges,
  progressBarFormValues,
  progressBarOffers
}) => {
  const offerDesignData = getOfferDesign.data;
  const { priceDisplay, priceInUsdCents } = usePriceForPreview({
    price,
    setAsFree
  });

  const productsList = useMemo(() => {
    if (!productsListForPreview.length) return [];

    const hasSale = (salePercentage ?? 0) > 0;
    if (!hasSale) return productsListForPreview;

    return productsListForPreview.map((product, index) => {
      if (index !== 0) return product;
      return {
        ...product,
        strokedSaleQuantity: String(
          product.quantity ?? product.quantityDisplay ?? ''
        )
      };
    });
  }, [productsListForPreview, salePercentage]);

  const progressBarPoints = useMemo(
    () =>
      resolveProgressBarPoints({
        progressBarId: progressBarFormValues?.progressBarId,
        points: progressBarFormValues?.progressBarPoints,
        progressBarOffers: progressBarOffers ?? []
      }),
    [progressBarFormValues, progressBarOffers]
  );

  return (
    <OfferPreviewShell width={PROMOTION_PREVIEW_WIDTH}>
      {({ generalTheme, storeScreen }) => {
        if (!offerDesignData) return null;

        const internalModel =
          generalTheme.specialOffersInternalViewModel ||
          ESpecialOfferInternalViewModel.HUGE;
        const specialOffer = offerDesignData.specialOffer;

        return renderingUtil.renderSpecialOfferByInternalModel(internalModel, {
          title: specialOffer?.title || '',
          fontSize: specialOffer?.fontSize || PROMOTION_DEFAULTS.fontSize,
          fontWeight: (specialOffer?.fontWeight ||
            PROMOTION_DEFAULTS.fontWeight) as EFontWeights,
          textColor: specialOffer?.fontColor || PROMOTION_DEFAULTS.fontColor,
          subTitle: specialOffer?.subTitle?.text || '',
          subTitleSize:
            specialOffer?.subTitle?.fontSize || PROMOTION_DEFAULTS.subFontSize,
          subTitleWeight: (specialOffer?.subTitle?.fontWeight ||
            PROMOTION_DEFAULTS.fontWeight) as EFontWeights,
          subTitleColor:
            specialOffer?.subTitle?.fontColor || PROMOTION_DEFAULTS.fontColor,
          ...(offerDesignData.backgroundImage && {
            backgroundImage: offerDesignData.backgroundImage
          }),
          ...(specialOffer?.backgroundColor && {
            backgroundColor: normalizeBgColor(specialOffer.backgroundColor)
          }),
          borderColor: offerDesignData.borderColor,
          buttonColor: storeScreen.buttonColor,
          design: 'match',
          prices: { priceDisplay, priceInUsdCents },
          productsList,
          selectOffer: () => {},
          isFree: setAsFree,
          collectText:
            specialOffer?.collectText || PROMOTION_DEFAULTS.collectText,
          salePercentage,
          salePercentageDisplayType,
          emblemBadges,
          ribbonBadges,
          progressBarPoints,
          ...(!hidePlayerAvailability &&
            playerAvailability != null && {
              availability: {
                total: playerAvailability,
                remaining: playerAvailability
              }
            })
        });
      }}
    </OfferPreviewShell>
  );
};
